/**
 * Shared randomization detection for canvas, WebGL and audio.
 *
 * Two complementary checks:
 *  - Repeat: render the same thing twice. Different output means per-read randomization
 *    (e.g. CanvasBlocker-style extensions).
 *  - Known answer: draw something whose exact pixels / samples we know in advance and read it
 *    back. Any difference means noise is being injected, even when the noise is held constant for
 *    the session (Brave "farbling", Firefox and Safari fingerprinting protection), which the
 *    repeat check alone cannot see.
 */

export type RandomizationState = 'none' | 'randomized' | 'noise' | 'blocked' | 'unavailable';
export type KnownAnswer = 'ok' | 'altered' | 'blocked' | 'unavailable';

export interface RandomizationInput {
  first: string | null;
  second: string | null;
  /** Output of an empty render; equal to `first` means readback was blocked. */
  blank?: string | null;
  knownAnswer: KnownAnswer;
}

export function classifyRandomization(input: RandomizationInput): RandomizationState {
  const { first, second, blank, knownAnswer } = input;
  if (first === null) return 'unavailable';
  if (knownAnswer === 'blocked' || (blank !== undefined && blank !== null && first === blank)) return 'blocked';
  if (second !== null && first !== second) return 'randomized';
  if (knownAnswer === 'altered') return 'noise';
  return 'none';
}

export const RANDOMIZATION_TEXT: Record<RandomizationState, string> = {
  none: 'Not detected: output is identical on every read and matches the expected test pattern.',
  randomized: 'Randomization active: the same drawing produced different output on two reads.',
  noise: 'Noise injection active: a known test pattern came back altered (consistent within this session).',
  blocked: 'Readback blocked: the browser returned blank or uniform data.',
  unavailable: 'API unavailable, so there was nothing to test.',
};

/** True when the state means a tracker cannot get a stable value from this surface. */
export const isProtected = (s: RandomizationState) => s === 'randomized' || s === 'noise' || s === 'blocked';

/**
 * Compare RGBA pixel data against an expected-color function. Returns 'blocked' for uniformly
 * empty or white data, 'altered' for any mismatch, 'ok' for an exact match.
 */
export function checkPixels(
  data: ArrayLike<number>,
  width: number,
  expected: (x: number, y: number) => readonly [number, number, number, number],
): KnownAnswer {
  let allZero = true;
  let allWhite = true;
  let mismatches = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const a = data[i + 3] ?? 0;
    if (r || g || b || a) allZero = false;
    if (r !== 255 || g !== 255 || b !== 255) allWhite = false;
    const px = i / 4;
    const [er, eg, eb, ea] = expected(px % width, Math.floor(px / width));
    if (r !== er || g !== eg || b !== eb || a !== ea) mismatches++;
  }
  if (allZero || allWhite) return 'blocked';
  return mismatches > 0 ? 'altered' : 'ok';
}
